import { useEffect, useRef, useState } from "react";

/** Ease-out cubic: quick to start, settling at the end. */
export function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Runs a 0→1 eased progress value over `duration` milliseconds, after `delay`,
 * via requestAnimationFrame.
 *
 * requestAnimationFrame rather than a CSS transition because the app's money
 * animations are sequences: several arcs driven from the same clock, each
 * waiting for the one before it. Expressing "start 900ms in" as a delay on a
 * shared timeline is exact; chaining transition-end events is not.
 *
 * A user who has asked the OS to reduce motion gets the final state
 * immediately. This is the only reduced-motion coverage a new animation gets —
 * the media query in app.css is scoped to the confetti and celebration
 * selectors — so anything that moves should be driven from here.
 *
 * `resetKey` replays the animation when it changes. Without it the effect is
 * keyed only on its timings, so a second transfer through a component that
 * stayed mounted would show a chart already at its end state.
 */
export function useGrow(
  duration: number,
  delay = 0,
  resetKey?: unknown,
): number {
  const [value, setValue] = useState(0);
  const frame = useRef<number | undefined>(undefined);

  useEffect(() => {
    const reduced = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduced) {
      setValue(1);
      return;
    }

    setValue(0);

    let start: number | null = null;
    const step = (now: number) => {
      if (start === null) start = now;
      const elapsed = now - start - delay;
      if (elapsed < 0) {
        frame.current = requestAnimationFrame(step);
        return;
      }
      const t = Math.min(1, elapsed / duration);
      setValue(easeOut(t));
      if (t < 1) frame.current = requestAnimationFrame(step);
    };

    frame.current = requestAnimationFrame(step);
    return () => {
      if (frame.current !== undefined) cancelAnimationFrame(frame.current);
    };
  }, [duration, delay, resetKey]);

  return value;
}

/** Interpolates between two numbers by an eased progress value. */
export function lerp(from: number, to: number, progress: number): number {
  return from + (to - from) * progress;
}
